// ===========================================
// NOTIFICATION SERVICE
// Sends notifications via Edge Function (same as FreshaApp)
// ===========================================

import { supabase } from '../config/supabase'
import { isDemoMode } from '../utils/demoMode'
import { applySalonFilter, getSelectedSalonId, belongsToSalon } from '../utils/salonScope'
import {
  demoAddNotification,
  demoGetNotifications,
  demoGetUnreadNotificationCount,
  demoMarkAllNotificationsRead,
  demoMarkNotificationRead,
} from '../utils/demoStore'

// Get Supabase URL from environment or config
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

// Validate environment variables
if (!supabaseUrl || !supabaseAnonKey) {
  console.error('❌ Missing Supabase environment variables in notificationService')
}

const EDGE_FUNCTION_URL = supabaseUrl ? `${supabaseUrl}/functions/v1/add-notification` : null

/**
 * Send a notification to a user using the Edge Function
 * @param {string} userId - User ID (UUID)
 * @param {string} title - Notification title
 * @param {string} message - Notification message
 * @param {string} type - Notification type (appointment, payment, favorite, etc.)
 * @param {string} relatedId - Optional related ID (appointment ID, payment ID, etc.)
 * @returns {Promise<{success: boolean, error?: string, notification?: object}>}
 */
/**
 * Append salon/admin cancellation note to in-app notification body (push uses same text where applicable).
 */
export const appendAdminCancellationReason = (message, adminCancellationReason) => {
  const t = typeof adminCancellationReason === 'string' ? adminCancellationReason.trim() : ''
  if (!t) return message || ''
  const base = message || ''
  return `${base}\n\nSalon message: ${t}`
}

export const sendNotification = async (userId, title, message, type, relatedId = null, salonId = null) => {
  try {
    // Campaign demo: keep the notification in the local session store only
    if (isDemoMode()) {
      const notification = demoAddNotification({ title, message, type, relatedId })
      return { success: true, notification }
    }

    if (!userId || !title || !message || !type) {
      throw new Error('Missing required parameters: userId, title, message, type')
    }

    // Validate environment variables
    if (!EDGE_FUNCTION_URL || !supabaseAnonKey) {
      throw new Error('Supabase configuration is missing. Please check your environment variables.')
    }

    // Get auth token for authenticated request
    const { data: { session } } = await supabase.auth.getSession()
    
    if (!session?.access_token) {
      throw new Error('User not authenticated')
    }

    const scopedSalonId = salonId || getSelectedSalonId() || null

    const response = await fetch(EDGE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': supabaseAnonKey, // Use anon key for apikey header
      },
      body: JSON.stringify({
        userId,
        title,
        message,
        type,
        relatedId,
        salonId: scopedSalonId,
      }),
    })

    const result = await response.json()

    if (!response.ok || !result.success) {
      throw new Error(result.error || 'Failed to send notification')
    }

    console.log('✅ Notification sent successfully:', result)
    return { success: true, notification: result.notification }
  } catch (error) {
    console.error('❌ Error sending notification:', error)
    return {
      success: false,
      error: error.message || 'Unknown error',
    }
  }
}

/**
 * Get notifications for the current user
 * @param {number} limit - Maximum number of notifications to fetch
 * @param {boolean} unreadOnly - If true, only fetch unread notifications
 * @returns {Promise<{data: array, error: object|null}>}
 */
export const getNotifications = async (limit = 50, unreadOnly = false, options = {}) => {
  try {
    const salonId = options.salonId || getSelectedSalonId()
    if (isDemoMode()) {
      const demoData = demoGetNotifications(limit, unreadOnly).filter((n) => belongsToSalon(n, salonId))
      return { data: demoData, error: null }
    }

    const { data: { user } } = await supabase.auth.getUser()
    
    if (!user) {
      return { data: [], error: { message: 'User not authenticated' } }
    }

    let query = supabase
      .from('notifications')
      // Select legacy + new-schema fields to support both notification schemas.
      .select('id, user_id, title, body, message, data, type, related_id, read, created_at, salon_id')
      .eq('user_id', user.id)
    query = applySalonFilter(query, { salonId })
    query = query.order('created_at', { ascending: false }).limit(limit)

    if (unreadOnly) {
      query = query.eq('read', false)
    }

    const { data, error } = await query

    if (error) throw error

    const normalized = (data || [])
      .filter((row) => belongsToSalon(row, salonId))
      .map((row) => ({
      ...row,
      message: row.body || row.message || '',
      type: row.type || row.data?.type || '',
      related_id:
        row.related_id ||
        row.data?.relatedId ||
        row.data?.appointment_id ||
        row.data?.appointmentId ||
        null,
      data: row.data || null,
    }))

    return { data: normalized, error: null }
  } catch (error) {
    console.error('Error fetching notifications:', error)
    return { data: [], error }
  }
}

/**
 * Mark a notification as read
 * @param {string} notificationId - Notification ID
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const markNotificationAsRead = async (notificationId) => {
  try {
    if (isDemoMode()) {
      demoMarkNotificationRead(notificationId)
      return { success: true, error: null }
    }

    const { data: { user } } = await supabase.auth.getUser()
    
    if (!user) {
      return { success: false, error: 'User not authenticated' }
    }

    const { error } = await supabase
      .from('notifications')
      .update({ read: true })
      .eq('id', notificationId)
      .eq('user_id', user.id)

    if (error) throw error

    return { success: true }
  } catch (error) {
    console.error('Error marking notification as read:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Mark all notifications as read for the current user
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const markAllNotificationsAsRead = async () => {
  try {
    if (isDemoMode()) {
      demoMarkAllNotificationsRead()
      return { success: true, error: null }
    }

    const { data: { user } } = await supabase.auth.getUser()
    
    if (!user) {
      return { success: false, error: 'User not authenticated' }
    }

    let unreadQuery = supabase
      .from('notifications')
      .update({ read: true })
      .eq('user_id', user.id)
      .eq('read', false)
    unreadQuery = applySalonFilter(unreadQuery, { salonId: getSelectedSalonId() })
    const { error } = await unreadQuery

    if (error) throw error

    return { success: true }
  } catch (error) {
    console.error('Error marking all notifications as read:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Delete a notification
 * @param {string} notificationId - Notification ID
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const deleteNotification = async (notificationId) => {
  try {
    if (isDemoMode()) {
      demoMarkNotificationRead(notificationId)
      return { success: true, error: null }
    }

    const { data: { user } } = await supabase.auth.getUser()
    
    if (!user) {
      return { success: false, error: 'User not authenticated' }
    }

    const { error } = await supabase
      .from('notifications')
      .delete()
      .eq('id', notificationId)
      .eq('user_id', user.id)

    if (error) throw error

    return { success: true }
  } catch (error) {
    console.error('Error deleting notification:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get unread notification count
 * @returns {Promise<{count: number, error: object|null}>}
 */
export const getUnreadNotificationCount = async (options = {}) => {
  try {
    const salonId = options.salonId || getSelectedSalonId()
    if (isDemoMode()) {
      const demoData = demoGetNotifications(50, true).filter((n) => belongsToSalon(n, salonId))
      return { count: demoData.length, error: null }
    }

    const { data: { user } } = await supabase.auth.getUser()
    
    if (!user) {
      return { count: 0, error: { message: 'User not authenticated' } }
    }

    let countQuery = supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id)
      .eq('read', false)
    countQuery = applySalonFilter(countQuery, { salonId })
    const { count, error } = await countQuery

    if (error) throw error

    return { count: count || 0, error: null }
  } catch (error) {
    console.error('Error getting unread notification count:', error)
    return { count: 0, error }
  }
}

/**
 * Notification type constants (same as FreshaApp)
 */
export const NOTIFICATION_TYPES = {
  APPOINTMENT_BOOKED: 'appointment_booked',
  APPOINTMENT_UPDATED: 'appointment_updated',
  APPOINTMENT_REASSIGNMENT: 'appointment_reassignment',
  APPOINTMENT_RESCHEDULED: 'appointment_rescheduled',
  APPOINTMENT_CANCELLED: 'appointment_cancelled',
  APPOINTMENT_COMPLETED: 'appointment_completed',
  APPOINTMENT_REMINDER: 'appointment_reminder',
  PAYMENT_SUCCESS: 'payment_success',
  PAYMENT_FAILED: 'payment_failed',
  FAVORITE_ADDED: 'favorite_added',
  FAVORITE_REMOVED: 'favorite_removed',
  PROFILE_CREATED: 'profile_created',
  PROFILE_UPDATED: 'profile_updated',
  SUPPORT_RESOLVED: 'support_resolved',
}

